import { getMessageFromTrezorErrorCode } from '@ambire-common/libs/trezor/trezor'
import trezorConnect from '@trezor/connect-web'

// Phase 4 (keystore isolation) — Trezor on mobile-web.
//
// `MainController` (and the `TrezorSigner` it routes signing through) runs inside
// a Web Worker, which has no DOM, no `window.open`, and no `navigator` popup
// host. The Trezor SDK needs all three to spawn the `connect.trezor.io` popup
// and talk to the device, so the REAL SDK must live on the PAGE.
//
// The extension uses `@trezor/connect-webextension`, which proxies through the
// MV3 background + an extension tab — plumbing that does not exist on a plain
// page. The plain-web flavor `@trezor/connect-web` (version-matched to the
// webextension build) opens its own popup window instead, which is the correct
// transport for the mobile-web SPA.
//
// This controller is the page half of the bridge. The worker holds a thin proxy
// (`trezorWorkerProxy.ts`) whose method calls round-trip here over `postMessage`
// (see `WebViewWorker.web.tsx` `hwTrezorInvoke`). Only non-secret data crosses:
// derived public addresses, unsigned payloads and returned signatures. The
// decrypted keystore `#mainKey`/seed never leave the worker, and every device
// operation requires physical on-device confirmation, so running the signer
// transport on the page does not widen the keystore-isolation attack surface.

const TREZOR_CONNECT_MANIFEST = {
  email: 'support@libertyswap.finance',
  appUrl: 'https://zkxwallet.com/',
  appName: 'ZKX',
  appIcon: ''
}

class TrezorPageController {
  walletSDK = trezorConnect

  isInitiated = false

  unlockedPath = ''

  unlockedPathKeyAddr = ''

  deviceModel = 'unknown'

  deviceId = ''

  // Resolves when the one-shot SDK init completes. The worker proxy treats the
  // controller as always-initiated; an init failure surfaces here and is
  // re-thrown on the first device call so the signer reports a real error.
  initialLoadPromise: Promise<void>

  constructor() {
    this.walletSDK.on('DEVICE_EVENT', (event: any) => {
      if (event?.payload?.name) {
        this.deviceModel = event.payload.name.replace(/^Trezor\s*/, '').trim()
      }
      if (event?.payload?.id) {
        this.deviceId = event.payload.id
      }
    })

    this.initialLoadPromise = this.#init()
  }

  async #init() {
    try {
      // `popup: true` opens the connect.trezor.io popup window (the plain-web
      // transport). `lazyLoad: true` defers the iframe until the first call.
      await this.walletSDK.init({ manifest: TREZOR_CONNECT_MANIFEST, lazyLoad: true, popup: true })
      this.isInitiated = true
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('TrezorPageController: failed to init the Trezor SDK', error)
    }
  }

  isUnlocked(path?: string, expectedKeyOnThisPath?: string) {
    if (!path || !expectedKeyOnThisPath) {
      return !!(this.unlockedPath && this.unlockedPathKeyAddr)
    }
    return this.unlockedPathKeyAddr === expectedKeyOnThisPath && this.unlockedPath === path
  }

  // Mirrors the extension `TrezorController.unlock`, but returns the resulting
  // unlocked state so the worker proxy can sync its own synchronous mirror
  // (`isUnlocked` is read synchronously inside the signer).
  async unlock(path: string, expectedKeyOnThisPath?: string) {
    if (this.isUnlocked(path, expectedKeyOnThisPath)) {
      return {
        status: 'ALREADY_UNLOCKED' as const,
        unlockedPath: this.unlockedPath,
        unlockedPathKeyAddr: this.unlockedPathKeyAddr
      }
    }

    const response = await this.walletSDK.ethereumGetAddress({
      path,
      showOnTrezor: false // prioritize having less steps for the user
    })

    if (!response.success) {
      throw new Error(getMessageFromTrezorErrorCode(response.payload.code, response.payload.error))
    }

    this.unlockedPath = response.payload.serializedPath
    this.unlockedPathKeyAddr = response.payload.address

    return {
      status: 'JUST_UNLOCKED' as const,
      unlockedPath: this.unlockedPath,
      unlockedPathKeyAddr: this.unlockedPathKeyAddr
    }
  }

  // The connect-web popup closes itself after each interaction, so there is no
  // stray window to remove (unlike the extension's window-manager popup). Kept
  // for parity with the `ExternalSignerController` shape the signer expects.
  // eslint-disable-next-line class-methods-use-this
  async signingCleanup() {}
}

export default TrezorPageController
